import React, { useState, useCallback, useMemo } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography
} from '@mui/material';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  BottomNavigation, 
  BottomNavigationAction,
  Divider,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  Settings as SettingsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

interface MobileNavigationProps {
  user?: {
    name: string;
    email: string;
    avatar?: string;
    role?: string;
    subBrokerLevel?: string;
  };
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bottomNavValue, setBottomNavValue] = useState(0);
  
  // Check if user is a sub-broker (role 'user' with subBrokerLevel set)
  const isSubBroker = user?.role === 'user' && user?.subBrokerLevel;

  // Sub-brokers only see their dashboard - no other menu items
  const primaryNavItems = useMemo(() => isSubBroker ? [] : [
    { label: 'Dashboard', path: '/utilities/dashboard', icon: <DashboardIcon /> },
    { label: 'Clients', path: '/utilities/clients', icon: <PeopleIcon /> },
    { label: 'Contracts', path: '/utilities/contracts', icon: <DescriptionIcon /> },
    { label: 'Admin', path: '/utilities/admin', icon: <SettingsIcon /> }
  ], [isSubBroker]);

  const secondaryNavItems = useMemo(() => isSubBroker ? [] : [
    { label: 'Properties', path: '/utilities/properties', icon: <HomeIcon /> }
  ], [isSubBroker]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleBottomNavChange = (event: React.SyntheticEvent, newValue: number) => {
    setBottomNavValue(newValue);
    if (primaryNavItems[newValue]) {
      navigate(primaryNavItems[newValue].path);
    }
  };

  const getCurrentBottomNavValue = useCallback(() => {
    const currentPath = location.pathname;
    const index = primaryNavItems.findIndex(item => 
      currentPath.startsWith(item.path) || 
      (item.path === '/utilities/dashboard' && currentPath === '/utilities')
    );
    return index >= 0 ? index : 0;
  }, [location.pathname, primaryNavItems]);

  React.useEffect(() => {
    setBottomNavValue(getCurrentBottomNavValue());
  }, [getCurrentBottomNavValue]);

  const drawerContent = (
    <Box sx={{ width: 280, height: '100%', bgcolor: '#1e293b', color: 'white' }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        bgcolor: '#0f172a', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 40, height: 40, bgcolor: '#3b82f6' }}>
            {user?.name?.charAt(0) || 'U'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'white' }}>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, color: 'white' }}>
              {user?.email || 'user@example.com'}
            </Typography>
          </Box>
        </Box>
        <IconButton 
          onClick={handleDrawerToggle}
          sx={{ color: 'primary.contrastText' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Primary Navigation */}
      <Box sx={{ px: 2, pt: 1, pb: 2 }}>
        <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem' }}>
          Main
        </Typography>
      </Box>
      <List sx={{ pt: 0 }}>
        {primaryNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname.startsWith(item.path)}
              sx={{
                mx: 1,
                borderRadius: 1,
                color: 'white',
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: '#3b82f6',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white'
                  }
                },
                '&:hover': {
                  bgcolor: '#334155'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                sx={{ 
                  '& .MuiTypography-root': { 
                    fontSize: '0.9rem',
                    fontWeight: 500
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2, borderColor: '#334155' }} />

      {/* Secondary Navigation */}
      <Box sx={{ px: 2, pt: 1, pb: 2 }}>
        <Typography variant="overline" sx={{ color: '#94a3b8', fontWeight: 'bold', fontSize: '0.75rem' }}>
          Management
        </Typography>
      </Box>
      <List sx={{ pt: 0 }}>
        {secondaryNavItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname.startsWith(item.path)}
              sx={{
                mx: 1,
                borderRadius: 1,
                color: 'white',
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: '#3b82f6',
                  color: 'white',
                  '& .MuiListItemIcon-root': {
                    color: 'white'
                  }
                },
                '&:hover': {
                  bgcolor: '#334155'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                sx={{ 
                  '& .MuiTypography-root': { 
                    fontSize: '0.9rem',
                    fontWeight: 500
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (!isMobile) {
    return null; // Use existing desktop navigation
  }

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: '#0f172a',
          borderBottom: '1px solid #334155',
          height: '56px'
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: 'white' }}>
            Utilities CRM
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 280,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Bottom Navigation */}
      <Paper 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          zIndex: theme.zIndex.appBar 
        }} 
        elevation={3}
      >
        <BottomNavigation
          value={bottomNavValue}
          onChange={handleBottomNavChange}
          showLabels
          sx={{ height: 70 }}
        >
          {primaryNavItems.slice(0, 4).map((item, index) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
              sx={{
                '&.Mui-selected': {
                  color: 'primary.main'
                }
              }}
            />
          ))}
        </BottomNavigation>
      </Paper>

      {/* Content spacing for mobile */}
      <Toolbar /> {/* AppBar spacing */}
      <Box sx={{ height: 70 }} /> {/* Bottom navigation spacing */}
    </>
  );
};

export default MobileNavigation;
